import { useState } from 'react';
import { useLocation, useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext.jsx';
import { authApi } from '../api/auth.js';
import { setTokens, setApiToken } from '../api/client.js';

export default function MfaVerify() {
  const { verifyMfa } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const challengeId = location.state?.challengeId;
  // Sanitize `from` to same-origin paths only — prevents open-redirect if
  // state was set to an external URL (e.g. by a crafted navigation call).
  const rawFrom = location.state?.from || '/dashboard';
  const from = typeof rawFrom === 'string' && rawFrom.startsWith('/') ? rawFrom : '/dashboard';

  const [mode, setMode] = useState('totp'); // totp | backup
  const [code, setCode] = useState('');
  const [backupCode, setBackupCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  if (!challengeId) return <Navigate to="/login" replace />;

  async function onSubmitTotp(e) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await verifyMfa(challengeId, code);
      navigate(from, { replace: true });
    } catch (err) {
      setError(err.response?.data?.message || 'MFA verification failed');
    } finally {
      setSubmitting(false);
    }
  }

  async function onSubmitBackup(e) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const data = await authApi.loginBackup(challengeId, backupCode.trim());
      setTokens({ accessToken: data.accessToken, refreshToken: data.refreshToken });
      setApiToken(data.accessToken);
      // Force a hard reload to refresh the AuthContext (simplest path).
      window.location.href = from;
    } catch (err) {
      setError(err.response?.data?.message || 'Invalid backup code');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="centered-screen">
      <form className="card auth-form" onSubmit={mode === 'totp' ? onSubmitTotp : onSubmitBackup}>
        <h1>Two-factor authentication</h1>
        {mode === 'totp' && (
          <>
            <p className="muted">Enter the 6-digit code from your authenticator app.</p>
            <label>
              Code
              <input
                inputMode="numeric"
                pattern="\d{6}"
                maxLength={6}
                autoComplete="one-time-code"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                required
              />
            </label>
            <a
              href="#"
              onClick={(e) => { e.preventDefault(); setMode('backup'); setError(null); }}
              style={{ display: 'block', fontSize: 12, marginTop: 4 }}
            >
              Use a backup code instead
            </a>
            {error && <div className="alert error" role="alert">{error}</div>}
            <button type="submit" className="btn primary" disabled={submitting || code.length !== 6}>
              {submitting ? 'Verifying…' : 'Verify'}
            </button>
          </>
        )}
        {mode === 'backup' && (
          <>
            <p className="muted">Enter one of your single-use backup codes (format: XXXX-XXXX).</p>
            <label>
              Backup code
              <input
                value={backupCode}
                onChange={(e) => setBackupCode(e.target.value.toUpperCase())}
                placeholder="XXXX-XXXX"
                required
                style={{ fontFamily: 'monospace', letterSpacing: 2 }}
              />
            </label>
            <a
              href="#"
              onClick={(e) => { e.preventDefault(); setMode('totp'); setError(null); }}
              style={{ display: 'block', fontSize: 12, marginTop: 4 }}
            >
              ← Use authenticator code instead
            </a>
            {error && <div className="alert error" role="alert">{error}</div>}
            <button type="submit" className="btn primary" disabled={submitting || backupCode.length < 8}>
              {submitting ? 'Verifying…' : 'Sign in with backup code'}
            </button>
          </>
        )}
      </form>
    </div>
  );
}
